// Sign-in (mock 38), recovery notice and activation (PD-009) for organisation users and, with
// `surface="platform"`, for system administrators (PD-003). SSO appears only when configured.
import type { ActivationInspectResponse, AuthContextResponse, MfaChallengeResponse, PlatformSessionResponse, SessionResponse } from '@passage/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { ApiRequestError, api, get, setCsrfToken } from '../api.ts';
import { MfaEnrol, MfaVerifyStep, RecoveryCodes } from '../components/Mfa.tsx';
import { Brand, ErrorSummary, Field, Loading } from '../components/ui.tsx';
import { LanguageSwitch, UnreviewedNotice, useI18n } from '../i18n.tsx';
import { SESSION_QUERY_KEY } from '../session.tsx';
import { SsoButtons } from './SsoSignIn.tsx';
import { DemoAccountsPanel, useDemoAccounts } from '../components/demo-accounts.tsx'; // PD-749 (demodata stream)

export const PLATFORM_SESSION_KEY = ['platform-session'] as const;

export function AuthFrame({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  return (
    <div className="auth-page">
      <div className="auth-top">
        <div className="brand">
          <Brand />
        </div>
        <div className="auth-lang">
          <UnreviewedNotice />
          <LanguageSwitch />
        </div>
      </div>
      <main id="workspace">{children}</main>
      <div className="auth-foot">{t('auth.footer')}</div>
    </div>
  );
}

export function SignInPage({ surface = 'organisation' }: { surface?: 'organisation' | 'platform' }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const ctx = useQuery({ queryKey: ['auth-context'], queryFn: () => get<AuthContextResponse>('/api/v1/auth/context') });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState<MfaChallengeResponse | null>(null);
  const [codes, setCodes] = useState<{ codes: readonly string[]; session: SessionResponse | PlatformSessionResponse } | null>(null);
  const platform = surface === 'platform';
  const base = platform ? '/api/platform/v1' : '/api/v1';
  // PD-749: demo mode only — the endpoint answers 404 otherwise and nothing is shown.
  const demo = useDemoAccounts(surface);
  const [demoBusy, setDemoBusy] = useState<string | null>(null);
  const [landing, setLanding] = useState('/');

  // A session only exists after every required step (PD-020).
  function complete(s: SessionResponse | PlatformSessionResponse, to = landing) {
    setCsrfToken(s.csrfToken);
    qc.clear();
    qc.setQueryData(platform ? PLATFORM_SESSION_KEY : SESSION_QUERY_KEY, s);
    navigate(to, { replace: true });
  }

  async function signIn(id: string, secret: string, to = '/') {
    setBusy(true);
    setError(null);
    setLanding(to);
    try {
      const r = await api<SessionResponse | PlatformSessionResponse | MfaChallengeResponse>('POST', `${base}/auth/login`, { email: id, password: secret });
      setPassword('');
      if ('next' in r) setChallenge(r);
      else complete(r, to);
    } catch (err) {
      setPassword('');
      setError(err instanceof ApiRequestError ? (err.code === 'NETWORK' ? t('error.network') : err.message) : t('error.requestFailed'));
    } finally {
      setBusy(false);
      setDemoBusy(null);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError(t('auth.missing'));
      return;
    }
    await signIn(email, password);
  }

  const org = ctx.data?.organisation;
  const usable = platform || Boolean(org);
  if (codes) {
    return (
      <AuthFrame>
        <div className="auth-card">
          <RecoveryCodes codes={codes.codes} onDone={() => complete(codes.session)} />
        </div>
      </AuthFrame>
    );
  }
  if (challenge) {
    return (
      <AuthFrame>
        <div className="auth-card">
          <h1>{challenge.next === 'mfa' ? t('mfa.title') : t('mfa.enrol.title')}</h1>
          {challenge.next === 'mfa' ? (
            <MfaVerifyStep<SessionResponse | PlatformSessionResponse> base={base} challengeToken={challenge.challengeToken} onDone={complete} />
          ) : (
            <MfaEnrol<(SessionResponse | PlatformSessionResponse) & { recoveryCodes: string[] }>
              base={base}
              challengeToken={challenge.challengeToken}
              required
              onDone={(r) => {
                const { recoveryCodes, ...session } = r;
                setCodes({ codes: recoveryCodes, session: session as SessionResponse | PlatformSessionResponse });
              }}
            />
          )}
          <button className="button quiet" type="button" onClick={() => setChallenge(null)}>
            {t('recovery.back')}
          </button>
        </div>
      </AuthFrame>
    );
  }
  return (
    <AuthFrame>
      <div className="auth-card">
        <h1>{platform ? t('platform.title') : t('auth.title')}</h1>
        <p className="lede">{platform ? t('platform.signInLede') : org ? t('auth.ledeOrg', { org: org.name }) : t('auth.lede')}</p>
        {demo.data && usable ? (
          // PD-767: in the demo, point first-time visitors at the role accounts below the form.
          <div className="note info demo-jump" style={{ marginBlockStart: 16 }}>
            <strong>{t('demo.jump.title')}</strong> {t('demo.jump.body')}{' '}
            <a href="#demo-accounts-title">{t('demo.jump.link')}</a>
          </div>
        ) : null}
        {!platform && ctx.isSuccess && !org ? (
          <div className="note danger" role="alert" style={{ marginBlockStart: 24 }}>
            <strong>{t('auth.unknownOrg.title')}</strong>
            {t('auth.unknownOrg.body')}
          </div>
        ) : null}
        {!platform && ctx.data?.methods.sso && ctx.data.ssoProviders?.length ? (
          <div style={{ marginBlockStart: 30 }}>
            {/* ADR-011: one button per active identity provider of this organisation. */}
            <SsoButtons providers={ctx.data.ssoProviders} />
            <p className="lede" style={{ textAlign: 'center', marginBlockStart: 24 }}>
              {t('auth.orLocal')}
            </p>
          </div>
        ) : null}
        <form onSubmit={submit} noValidate aria-describedby={error ? 'signin-error' : undefined}>
          {error ? (
            <div className="note danger" role="alert" id="signin-error">
              {error}
            </div>
          ) : null}
          <Field label={t('auth.email')}>
            {(p) => <input {...p} type="email" dir="ltr" autoComplete="username" placeholder={t('auth.emailPlaceholder')} value={email} onChange={(e) => setEmail(e.target.value)} disabled={!usable} required />}
          </Field>
          <Field label={t('auth.password')}>
            {(p) => <input {...p} type="password" autoComplete="current-password" placeholder={t('auth.passwordPlaceholder')} value={password} onChange={(e) => setPassword(e.target.value)} disabled={!usable} required />}
          </Field>
          <button className="button dark" type="submit" disabled={busy || !usable}>
            {busy ? t('auth.submitting') : t('auth.submit')}
          </button>
          {!platform ? (
            <Link className="button" to="/account-recovery">
              {t('auth.recovery')}
            </Link>
          ) : null}
        </form>
        <p className="fineprint">
          {t('auth.fineprint1')}
          <br />
          {t('auth.fineprint2')}
        </p>
      </div>
      {demo.data && usable ? (
        <div className="auth-demo">
          <DemoAccountsPanel
            data={demo.data}
            surface={surface}
            busyKey={demoBusy}
            onSignIn={(account, secret, to) => {
              // "Sign in as …" fills the form (visible to the user) and submits it.
              setEmail(account.email);
              setPassword(secret);
              setDemoBusy(account.key);
              void signIn(account.email, secret, to);
            }}
          />
        </div>
      ) : null}
    </AuthFrame>
  );
}

export function RecoveryPage() {
  const { t } = useI18n();
  return (
    <AuthFrame>
      <div className="auth-card">
        <h1>{t('recovery.title')}</h1>
        <p className="lede">{t('recovery.lede')}</p>
        <div className="note" style={{ marginBlockStart: 24 }}>
          {t('recovery.body')}
        </div>
        <Link className="button" to="/sign-in">
          {t('recovery.back')}
        </Link>
      </div>
    </AuthFrame>
  );
}

/** PD-009: the token arrives in the URL fragment (never sent to a server log) and is posted in a body. */
export function ActivatePage({ surface = 'organisation' }: { surface?: 'organisation' | 'platform' }) {
  const { t } = useI18n();
  const base = surface === 'platform' ? '/api/platform/v1' : '/api/v1';
  const [token] = useState(() => new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token') ?? '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const info = useQuery({
    queryKey: ['activation', surface, token],
    queryFn: () => api<ActivationInspectResponse>('POST', `${base}/auth/activation/inspect`, { token }),
    enabled: token.length > 20,
    retry: false,
  });
  const m = useMutation({ mutationFn: () => api('POST', `${base}/auth/activation`, { token, password }) });
  // Clear the secret from the address bar and history once read.
  useEffect(() => {
    if (window.location.hash) history.replaceState(null, '', window.location.pathname);
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    m.mutate();
  };
  return (
    <AuthFrame>
      <div className="auth-card">
        <h1>{t('activate.title')}</h1>
        {!token || info.isError ? (
          <div className="note danger" role="alert" style={{ marginBlockStart: 24 }}>
            {t('activate.invalid')}
          </div>
        ) : info.isPending ? (
          <Loading label={t('activate.checking')} />
        ) : m.isSuccess ? (
          <>
            <div className="note" role="status" style={{ marginBlockStart: 24 }}>
              {t('activate.done')}
            </div>
            <Link className="button dark" to="/sign-in">
              {t('auth.submit')}
            </Link>
          </>
        ) : (
          <>
            <p className="lede">
              {t('activate.lede', { email: '⁨' + info.data.email + '⁩' })}
            </p>
            <form onSubmit={submit} noValidate>
              <ErrorSummary error={m.error} fieldIds={{ password: 'activate-password' }} />
              <Field id="activate-password" label={t('activate.password')} hint={t('activate.hint', { min: info.data.passwordMinLength })} error={m.error instanceof ApiRequestError ? m.error.fields.password : undefined}>
                {(p) => <input {...p} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={info.data.passwordMinLength} required />}
              </Field>
              <Field label={t('activate.confirm')} error={mismatch ? t('activate.mismatch') : undefined}>
                {(p) => <input {...p} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />}
              </Field>
              <button className="button dark" type="submit" disabled={m.isPending}>
                {m.isPending ? t('common.saving') : t('activate.submit')}
              </button>
            </form>
          </>
        )}
      </div>
    </AuthFrame>
  );
}
